import brand from '@helpers/brand';
import { fluidAdjusted } from '@helpers/fluid';
import { baseGrid } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import { gradientStyle, headingStyles, paragraphMedium } from '@helpers/typography';
import BurnEffect2 from '@img/burn-effect-2.png';
import ArrowSvg from '@img/shapes/arrow-mask.svg';
import { ButtonStyles } from '@stories/Components/Buttons/GeneralButton/Button.styles';
import { rgba } from 'polished';
import styled from 'styled-components';
const Container = styled.section `
  overflow: hidden;
  position: relative;
  display: block;
`;
const Grid = styled.div `
  ${baseGrid};
  align-items: center;
  position: relative;
  z-index: 2;

  @media ${until(Device.Tablet)} {
    row-gap: 32px;
  }
`;
const ContentArea = styled.div `
  position: relative;
  grid-column: span 12;
  grid-row: 2;

  @media ${from(Device.Tablet)} {
    grid-column: span 5;
    grid-row: 1;
  }

  [data-is-video='true'] & {
    @media ${from(Device.Tablet)} {
      grid-column: span 6;
    }
  }

  [data-media-alignment='left'] & {
    @media ${from(Device.Tablet)} {
      grid-column: 8 / span 5;
    }
  }

  [data-media-alignment='left'][data-is-video='true'] & {
    @media ${from(Device.Tablet)} {
      grid-column: 8 / span 5;
    }
  }

  ${ButtonStyles.Button} {
    margin-top: 15px;
  }
`;
const Title = styled.h3 `
  ${headingStyles};
  --titleColor: ${brand.secondary.red5};
  font-size: ${fluidAdjusted(32, 64)};
  line-height: ${fluidAdjusted(40, 74)};
  color: var(--titleColor);
  position: relative;
  padding-bottom: 7px;

  &[data-title-theme='mid'] {
    --titleColor: ${brand.secondary.red3};
  }

  &[data-title-theme='light'] {
    --titleColor: ${brand.secondary.red1};
  }

  &[data-title-theme='gradient'] {
    ${gradientStyle};

    [data-is-video='false'] & {
      &::before {
        content: '';
        height: 4px;
        width: 20px;
        background-color: ${brand.secondary.red5};
        position: absolute;
        top: ${fluidAdjusted(18, 40)};
        right: calc(100% + 10px);
        border-radius: 0 2px 2px 0;

        @media ${from(Device.Tablet)} {
          height: 6px;
          width: 72px;
          right: calc(100% + 20px);
          border-radius: 0 3px 3px 0;
        }

        @media ${from(Device.DesktopLarge)} {
          right: calc(100% + 40px);
        }
      }
    }
  }

  [data-media-alignment='left'][data-is-video='false'] & {
    &[data-title-theme='gradient'] {
      display: block;
      background: linear-gradient(90deg, var(--leftGradient), var(--rightGradient));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;

      &::before {
        content: none;
      }

      &::after {
        content: '';
        height: 4px;
        width: 20px;
        background-color: ${brand.secondary.red5};
        position: absolute;
        top: ${fluidAdjusted(18, 40)};
        right: calc(100% + 10px);
        border-radius: 0 2px 2px 0;

        @media ${from(Device.Tablet)} {
          height: 6px;
          width: 72px;
          right: auto;
          left: calc(100%);
          border-radius: 3px 0 0 3px;
        }
      }
    }
  }
`;
const Intro = styled.div `
  ${paragraphMedium};
  margin-bottom: 15px;
  max-width: 700px;
`;
const PlayBg = styled.div `
  position: absolute;
  top: 50%;
  left: 70%;
  transform: translateY(-50%) rotate(-20deg);
  width: 234px;
  height: 234px;
  color: ${brand.secondary.red1};
  opacity: 1%;
  z-index: -1;

  [data-media-alignment='right'] & {
    left: auto;
    right: 70%;
  }

  @media ${from(Device.Tablet)} {
    width: 546px;
    height: 546px;
  }
`;
const MediaArea = styled.div `
  grid-column: span 12;
  grid-row: 1;
  display: grid;

  @media ${from(Device.Tablet)} {
    grid-column: 7 / span 6;
    justify-content: end;
  }

  [data-is-video='true'] & {
    @media ${from(Device.Tablet)} {
      grid-column: 7 / span 6;
    }
  }

  [data-media-alignment='left'] & {
    @media ${from(Device.Tablet)} {
      grid-column: span 6;
    }
  }

  [data-media-alignment='left'][data-is-video='true'] & {
    @media ${from(Device.Tablet)} {
      grid-column: span 6;
    }
  }
`;
const MediaHolder = styled.div `
  max-width: 1100px;
  position: relative;
  left: var(--sitePadding);

  [data-media-alignment='left'] & {
    left: auto;
    right: var(--sitePadding);
  }
`;
const Image = styled.img `
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;
const BurnOverlay = styled.div `
  width: 143%;
  height: 132%;
  background-image: url(${BurnEffect2});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  position: absolute;
  top: 50%;
  left: 0;
  transform: translate(5%, -50%);
  mix-blend-mode: lighten;

  [data-media-alignment='left'] & {
    left: auto;
    right: 0;
    transform: translate(-5%, -50%) scaleX(-1);
  }
`;
const ShapeShadow = styled(ArrowSvg) `
  position: absolute;
  color: ${brand.buff.buff3};
  transform: translate(-6px, 10px);
  max-width: 1100px;

  @media ${until(Device.Tablet)} {
    transform: translate(6px, 10px);
  }

  [data-media-alignment='left'] & {
    transform: translate(6px, 10px) scaleX(-1);

    @media ${from(Device.Tablet)} {
      transform: translate(6px, 10px) scaleX(-1);
    }
  }
`;
const PlayButton = styled.button `
  position: absolute;
  top: 0;
  left: 0;
  border: none;
  background: ${rgba(brand.black, 0.4)};
  width: 100%;
  height: 100%;
  color: ${brand.white};

  svg {
    position: absolute;
    bottom: 10px;
    left: 10px;
    width: 48px;
    height: 48px;

    @media ${from(Device.Tablet)} {
      width: 96px;
      height: 96px;
      bottom: 20px;
      left: auto;
      right: 20px;
    }

    [data-media-alignment='left'] & {
      @media ${from(Device.Tablet)} {
        right: auto;
        left: 20px;
      }
    }
  }
`;
const SplitContentStyles = {
    BurnOverlay,
    Container,
    ContentArea,
    Grid,
    Image,
    Intro,
    MediaArea,
    MediaHolder,
    PlayBg,
    PlayButton,
    ShapeShadow,
    Title,
};
export default SplitContentStyles;
